/**
 * File Name          : projectile.component.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 * 
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Component, Input, SimpleChanges, ViewChild, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { ProjectileResults } from '../../../lesson-utils/projectile-analysis';
import { CommonModule } from '@angular/common';
import { DxButtonGroupModule, DxButtonModule, DxChartModule, DxPopupModule, DxChartComponent } from 'devextreme-angular';
import { variableSigFigsFormatter } from '../../../utils/formatters';
import { ScreenserviceService } from '../../../services/screenservice.service';
import { ProjectileFields } from '../../../models/lessons.model';
import { Subscription } from 'rxjs';

const AVAILABLE_SERIES = [
  {text: 'Velocity X (m/s)', valueField: ProjectileFields.VELOCITY_X_SERIES, type:'default'}, 
  {text: 'Velocity Y (m/s)', valueField: ProjectileFields.VELOCITY_Z_SERIES, type:'default'},
  {text: 'Height (m)', valueField: ProjectileFields.HEIGHT_SERIES, type:'default'}
]


@Component({
  selector: 'app-projectile',
  standalone: true,
  imports: [CommonModule, DxChartModule, DxButtonGroupModule, DxButtonModule, DxPopupModule],
  templateUrl: './projectile.component.html',
  styleUrl: './projectile.component.scss'
})
export class ProjectileComponent implements OnInit, OnDestroy {
  @Input() results: ProjectileResults | undefined = undefined;
  @Input() scalars: string[] = [];
  @Input() series: string[] = [];

  @ViewChild(DxChartComponent) chart?: DxChartComponent;

  mobileChartSelection: string|null = null;
  chartKey: number = 0; // Used to force chart re-render on orientation change

  // Cached result of computeFilteredData(), recomputed only when `results` changes (ngOnChanges)
  // - see the dataSource/tooltip comment in ngOnChanges for why this must stay a stable reference.
  private filteredData: ReturnType<ProjectileComponent['computeFilteredData']> = [];

  private orientationSub?: Subscription;

    ProjectileFields = ProjectileFields;

    constructor(
      public screenservice: ScreenserviceService,
      private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
      // Driven by the service's settled orientation signal rather than a raw
      // window resize/orientationchange listener - see ScreenserviceService for why.
      this.orientationSub = this.screenservice.orientationChanges.subscribe(() => this.onOrientationChange());
    }

    ngOnDestroy(): void {
      this.orientationSub?.unsubscribe();
    }

    onOrientationChange(): void {
      // Force chart re-render on orientation change
      this.chartKey++;
      this.cdr.detectChanges();

      // Give the DOM time to update, then force a full remeasure - without
      // { force: true } DevExtreme can skip re-measuring the container after
      // rotation, leaving the chart sized to a stale (pre-rotation) reading,
      // which is what produced the cramped/truncated layout on rotation.
      setTimeout(() => {
        if (this.chart?.instance) {
          this.chart.instance.render({ force: true });
        }
      }, 100);
    }

    // The mobile chart popup can be opened without any intervening rotation
    // event (device already in its current orientation) - the service's own
    // listeners never fire in that case, so isLandscape could still reflect a
    // stale read from before the page settled. Force a fresh check right as
    // the popup opens instead of relying solely on passive orientation events.
    openMobileChart(seriesValueField: string): void {
      this.screenservice.recheckOrientation();
      this.mobileChartSelection = seriesValueField;
      this.onOrientationChange();
    }

    // DevExtreme mounts the popup's content on its own show/animation lifecycle,
    // not synchronously with the click that sets mobileChartSelection - relying
    // solely on that click-time check left a window where the popup could still
    // render against a stale orientation read. onShowing re-validates right
    // before content mounts; onShown re-measures once the popup has finished
    // animating in and the chart has its final on-screen size.
    onPopupShowing(): void {
      this.screenservice.recheckOrientation();
    }

    onPopupShown(): void {
      this.onOrientationChange();
    }

    ngOnChanges(changes: SimpleChanges): void {``
      if (changes['scalars']) {
        // console.log('scalars changed:', changes['scalars'].currentValue);
      }
      if (changes['series']) {
        // console.log('series changed:', changes['series'].currentValue);
        this.availableDataSeries = AVAILABLE_SERIES.filter((s: any) => (changes['series'].currentValue as string[]).includes(s.valueField))
        this.selectedDataSeries = []
      }
      if (changes['results']) {
        // Recompute once per results change and cache the array - the dx-chart dataSource
        // was previously bound directly to getFilteredData(), called from the template.
        // That recomputed a fresh array (and objects) on every change-detection pass,
        // including the ones hover/mousemove inside the chart trigger, which made
        // DevExtreme treat the dataSource as constantly "changing" and reset/redraw the
        // chart mid-hover - tearing down the tooltip before it could render (20220).
        this.filteredData = this.computeFilteredData();
      }
    }


    localVariableSigFigsFormatter = variableSigFigsFormatter;

    availableDataSeries = AVAILABLE_SERIES as any;
    selectedDataSeries: string[] = [];
  
    isSeriesSelected(series: string): boolean {
      return this.selectedDataSeries.includes(series);
    }
    customizeTooltip = ({ argumentText, valueText }: Record<string, string>) => {
      return {
        text: `${Number(argumentText).toFixed(2)}s, ${Number(valueText).toFixed(2)}${this.getUnits()}`,
      };
    }
  
    getUnits = () => {
      if(this.isSeriesSelected('velocity')) return "m/s"
      if(this.isSeriesSelected('height')) return "m"
      return ""
    }
  
    roundDownChartLabel = (argument: { value: string | number | Date; valueText: string; }) => {
      const value = argument.value as number;
      return (value >= 0 ? Math.floor(value) : Math.ceil(value)).toString();
    }
  
    getChartPopupTitle = () => {
      return AVAILABLE_SERIES.find(s => s.valueField === this.mobileChartSelection)?.text ?? "Data Chart";
    }

    // Guards against a genuinely missing/non-numeric reading, should the analysis ever emit
    // one. In practice the 19821 gap turned out not to be this at all - see the value-axis
    // `wholeRange` comment in projectile.component.html for the actual root cause (velocity Y's
    // value axis was hard-floored at -1, clipping any point once the fall speed passed it).
    private static isMissing(value: unknown): boolean {
      return typeof value !== 'number' || !Number.isFinite(value);
    }

    getFilteredData() {
      return this.filteredData;
    }

    private computeFilteredData() {
      // Filter out missing values and ensure we only show data points with valid values
      const data = this.results?.hgtPlot_m ?? [];

      const filtered = data.filter(point =>
        point != null &&
        !ProjectileComponent.isMissing(point.time_s) &&
        (!ProjectileComponent.isMissing(point.hgt_m) || !ProjectileComponent.isMissing(point.velocityX) || !ProjectileComponent.isMissing(point.velocityZ))
      );

      // Hold each series at its last known value so every series' line reaches the same
      // shared end time instead of stopping short once the analysis stops computing it.
      let lastHgt: number | null = null;
      let lastVelX: number | null = null;
      let lastVelZ: number | null = null;
      return filtered.map(point => {
        if (!ProjectileComponent.isMissing(point.hgt_m)) lastHgt = point.hgt_m;
        if (!ProjectileComponent.isMissing(point.velocityX)) lastVelX = point.velocityX;
        if (!ProjectileComponent.isMissing(point.velocityZ)) lastVelZ = point.velocityZ;
        return { ...point, hgt_m: lastHgt, velocityX: lastVelX, velocityZ: lastVelZ };
      });
    }

    private static readonly SERIES_TO_DATA_FIELD: Record<string, 'hgt_m' | 'velocityX' | 'velocityZ'> = {
      velocityX: 'velocityX',
      velocityZ: 'velocityZ',
      height: 'hgt_m',
    };

    // getFilteredData() now holds every series at its last known value, so in practice all
    // series share the same end time; this still resolves each series' own last reading in
    // case a given series has no data at all (all missing), rather than assuming full duration.
    getAxisMaxTime = (seriesKey: string | null) => {
      const field = seriesKey ? ProjectileComponent.SERIES_TO_DATA_FIELD[seriesKey] : undefined;
      if (!field) return undefined;

      const data = this.getFilteredData();
      for (let i = data.length - 1; i >= 0; i--) {
        if (!ProjectileComponent.isMissing(data[i][field])) return data[i].time_s;
      }
      return undefined;
    }
}
